import React from 'react';
import Layout from '../components/Layout';
import MobileLayout from '../components/Mobile/MobileLayout';
import { useIsMobile } from '../hooks/useIsMobile';
import { Container, Card, Alert } from 'react-bootstrap';

const PrivacyPolicy: React.FC = () => {
  const isMobile = useIsMobile();

  const content = (
    <Container className="py-4">
      <Card className="shadow-sm">
        <Card.Body className="p-4 p-md-5">
          <h1 className="mb-4">Privacy Policy</h1>

          <div className="text-muted mb-4">
            Last Updated: {new Date().toLocaleDateString()}
          </div>

          <Alert variant="info" className="mb-4">
            Your privacy is important to us. This Privacy Policy explains how we collect,
            use, disclose, and safeguard your information when you use NayaBajar.
          </Alert>

          <section className="mb-5">
            <h2 className="mb-3">1. Information We Collect</h2>

            <h3 className="h5 mb-3">1.1 Personal Information</h3>
            <p className="mb-3">We collect information you provide directly:</p>
            <ul className="mb-4">
              <li>Account information: name, email, phone number, password</li>
              <li>Profile information: profile picture, bio, location</li>
              <li>Listings: product photos, descriptions, prices</li>
              <li>Communications: messages, inquiries, feedback</li>
            </ul>

            <h3 className="h5 mb-3">1.2 Automatically Collected Information</h3>
            <p className="mb-3">We automatically collect:</p>
            <ul>
              <li>Device information: IP address, browser type, operating system</li>
              <li>Usage data: pages visited, features used, time spent</li>
              <li>Location data: city, region based on IP address</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">2. How We Use Your Information</h2>
            <p className="mb-3">We use your information to:</p>
            <ul>
              <li>Provide, maintain, and improve our Service</li>
              <li>Process transactions and send related information</li>
              <li>Send technical notices and support messages</li>
              <li>Respond to comments, questions, and customer service</li>
              <li>Monitor and analyze trends, usage, and activities</li>
              <li>Detect, prevent, and address technical issues and fraud</li>
              <li>Personalize your experience and deliver relevant content</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">3. Information Sharing</h2>

            <h3 className="h5 mb-3">3.1 With Other Users</h3>
            <p className="mb-3">
              Your profile information (name, username, photo) may be visible to other users.
              Contact information is only shared if you choose to make it public.
            </p>

            <h3 className="h5 mb-3">3.2 Service Providers</h3>
            <p className="mb-3">
              We share data with third parties who perform services on our behalf:
            </p>
            <ul className="mb-4">
              <li>Hosting and cloud services</li>
              <li>Payment processors</li>
              <li>Analytics services</li>
              <li>Email and messaging services</li>
            </ul>

            <h3 className="h5 mb-3">3.3 Legal Requirements</h3>
            <p className="mb-3">
              We may disclose information if required to do so by law or in good faith belief:
            </p>
            <ul>
              <li>Comply with legal obligations</li>
              <li>Protect our rights and property</li>
              <li>Prevent fraud or security issues</li>
              <li>Protect users' safety</li>
            </ul>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">4. Data Security</h2>
            <p className="mb-3">
              We implement appropriate security measures to protect your information:
            </p>
            <ul>
              <li>HTTPS/SSL encryption for data transmission</li>
              <li>Password hashing and salting</li>
              <li>Regular security audits</li>
              <li>Access controls and authentication</li>
              <li>Secure data centers</li>
            </ul>
            <p className="mt-3">
              However, no method of transmission over the Internet is 100% secure.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">5. Cookies and Tracking</h2>
            <p className="mb-3">
              We use cookies and similar technologies to:
            </p>
            <ul className="mb-4">
              <li>Remember your preferences and keep you logged in</li>
              <li>Understand how you use our Service</li>
              <li>Analyze usage patterns and improve our features</li>
              <li>Personalize content and ads</li>
            </ul>

            <h3 className="h5 mb-3">5.1 Cookie Categories</h3>
            <ul className="mb-3">
              <li><strong>Essential Cookies:</strong> Required for basic functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand user behavior</li>
              <li><strong>Advertising Cookies:</strong> Deliver relevant advertisements</li>
              <li><strong>Preference Cookies:</strong> Remember your settings</li>
            </ul>

            <h3 className="h5 mb-3">5.2 Your Choices</h3>
            <p>
              You can manage cookie preferences through your browser settings.
              However, disabling essential cookies may affect Service functionality.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">6. Data Retention</h2>
            <p className="mb-3">
              We retain your personal data for:
            </p>
            <ul>
              <li>Account duration + 1 year after closure</li>
              <li>Required retention periods by law</li>
              <li>Legitimate business purposes</li>
            </ul>
            <p className="mt-3">
              After retention periods, data is securely deleted or anonymized.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">7. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="mb-3">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correct:</strong> Update inaccurate or incomplete data</li>
              <li><strong>Delete:</strong> Request deletion of your personal data</li>
              <li><strong>Object:</strong> Opt-out of certain data processing</li>
              <li><strong>Port:</strong> Transfer your data to another service</li>
              <li><strong>Withdraw Consent:</strong> Revoke previously given consent</li>
            </ul>
            <p>
              To exercise these rights, contact us at{' '}
              <a href="mailto:privacy@nayabajar.com" className="text-primary">
                privacy@nayabajar.com
              </a>
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">8. Children's Privacy</h2>
            <p>
              Our Service is not intended for children under 13. We do not knowingly collect
              personal information from children under 13. If you are a parent or guardian
              and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-5">
            <h2 className="mb-3">9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than Nepal.
              We ensure appropriate safeguards are in place to protect your data.
            </p>
          </section>

          <section>
            <h2 className="mb-3">10. Contact Us</h2>
            <p className="mb-3">
              For privacy-related questions or concerns:
            </p>
            <ul className="list-unstyled">
              <li><strong>Email:</strong>{' '}
                <a href="mailto:privacy@nayabajar.com" className="text-primary">
                  privacy@nayabajar.com
                </a>
              </li>
              <li><strong>Address:</strong> Kathmandu, Nepal</li>
            </ul>
          </section>
        </Card.Body>
      </Card>
    </Container>
  );

  if (isMobile) {
    return <MobileLayout>{content}</MobileLayout>;
  }

  return <Layout showSidebar={false}>{content}</Layout>;
};

export default PrivacyPolicy;
